/**
 * Load Label Renderer - Draws heating/cooling loads on floor plan
 * Displays BTU/hr values inside rooms
 */

import {
  calculateRoomHeatingLoad,
  calculateRoomCoolingLoad,
  formatLoad,
} from '../../utils/loadCalculations';

/**
 * Calculate room centroid from vertices/points
 */
function calculateCentroid(points) {
  if (!points || points.length < 3) return null;

  let sumX = 0;
  let sumY = 0;
  let area = 0;

  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    const cross = points[i].x * points[j].y - points[j].x * points[i].y;
    area += cross;
    sumX += (points[i].x + points[j].x) * cross;
    sumY += (points[i].y + points[j].y) * cross;
  }

  area /= 2;
  if (Math.abs(area) < 0.001) return null;

  return {
    x: sumX / (6 * area),
    y: sumY / (6 * area),
  };
}

/**
 * Draw a single room load label
 */
function drawRoomLoadLabel(ctx, room, roomData, designConditions, scale, options = {}) {
  const points = room.points || room.vertices;
  if (!points || points.length < 3) return;

  const centroid = calculateCentroid(points);
  if (!centroid) return;

  // Calculate loads
  const heatingLoad = calculateRoomHeatingLoad(roomData, designConditions);
  const coolingLoad = calculateRoomCoolingLoad(roomData, designConditions);

  const showHeating = options.showHeating !== false;
  const showCooling = options.showCooling !== false;
  const showCFM = options.showCFM === true;

  // Calculate CFM (approximate: total BTU / 30 for cooling)
  const cfm = showCFM ? Math.round(coolingLoad.total / 30) : 0;

  // Styling
  const fontSize = Math.max(10, 12 / scale);
  const padding = 4 / scale;
  const lineHeight = fontSize * 1.3;
  const cornerRadius = 4 / scale;

  // Build text lines
  const lines = [];

  if (showHeating && heatingLoad.total > 0) {
    lines.push({ text: `🔥 ${formatLoad(heatingLoad.total)}`, color: '#ff9800' });
  }

  if (showCooling && coolingLoad.total > 0) {
    lines.push({ text: `❄️ ${formatLoad(coolingLoad.total)}`, color: '#2196f3' });
  }

  if (showCFM && cfm > 0) {
    lines.push({ text: `💨 ${cfm} CFM`, color: '#4caf50' });
  }

  if (lines.length === 0) return;

  // Measure text
  ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;

  let maxWidth = 0;
  lines.forEach(line => {
    const metrics = ctx.measureText(line.text);
    maxWidth = Math.max(maxWidth, metrics.width);
  });

  const boxWidth = maxWidth + padding * 4;
  const boxHeight = lines.length * lineHeight + padding * 2;
  const boxX = centroid.x - boxWidth / 2;
  const boxY = centroid.y - boxHeight / 2;

  // Draw background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
  ctx.beginPath();
  ctx.roundRect(boxX, boxY, boxWidth, boxHeight, cornerRadius);
  ctx.fill();

  // Draw border
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
  ctx.lineWidth = 1 / scale;
  ctx.stroke();

  // Draw text
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  lines.forEach((line, index) => {
    const textY = boxY + padding + lineHeight * (index + 0.5);
    ctx.fillStyle = line.color;
    ctx.fillText(line.text, centroid.x, textY);
  });
}

/**
 * Draw load labels for all rooms
 */
export function drawAllRoomLoadLabels(ctx, rooms, roomsData, designConditions, scale, options = {}) {
  if (!rooms || rooms.length === 0) return;

  // Create a map of room data by roomId
  const roomDataMap = {};
  (roomsData || []).forEach(rd => {
    if (rd.roomId) {
      roomDataMap[rd.roomId] = rd;
    }
  });

  rooms.forEach(room => {
    // Skip unconditioned rooms
    if (room.name === 'garage' || room.conditioned === 'no') return;

    // Get room data (from hvacData or use room properties)
    let roomData = roomDataMap[room.id];

    // If no hvacData, create basic room data from room properties
    if (!roomData) {
      const points = room.points || room.vertices;
      let area = 0;
      if (points && points.length >= 3) {
        // Calculate area using shoelace formula
        for (let i = 0; i < points.length; i++) {
          const j = (i + 1) % points.length;
          area += points[i].x * points[j].y - points[j].x * points[i].y;
        }
        area = Math.abs(area) / 2 / (40 * 40); // Convert to sq ft
      }

      roomData = {
        roomId: room.id,
        name: room.name || 'bedroom',
        conditioned: 'yes',
        floorArea: Math.round(area) || room.area || 0,
        ceilingHeight: 8,
        ceilingType: 'below-vented-attic',
        windows: [],
        exteriorWalls: [],
        floorType: 'slab',
        floorInsulation: 'none',
        doors: [],
      };
    }

    if (roomData.conditioned === 'no') return;

    drawRoomLoadLabel(ctx, room, roomData, designConditions, scale, options);
  });
}

/**
 * Draw load label for a single selected room (highlighted)
 */
export function drawSelectedRoomLoadLabel(ctx, room, roomData, designConditions, scale) {
  if (!room) return;

  const points = room.points || room.vertices;
  if (!points || points.length < 3) return;

  const centroid = calculateCentroid(points);
  if (!centroid) return;

  // Calculate loads
  const heatingLoad = calculateRoomHeatingLoad(roomData, designConditions);
  const coolingLoad = calculateRoomCoolingLoad(roomData, designConditions);
  const cfm = Math.round(coolingLoad.total / 30);

  // Styling - larger for selected room
  const fontSize = Math.max(12, 14 / scale);
  const padding = 6 / scale;
  const lineHeight = fontSize * 1.3;
  const cornerRadius = 6 / scale;

  // Build detailed lines
  const lines = [
    { text: `Heating: ${formatLoad(heatingLoad.total)} BTU/hr`, color: '#ff9800' },
    { text: `Cooling: ${formatLoad(coolingLoad.total)} BTU/hr`, color: '#2196f3' },
    { text: `Sensible: ${formatLoad(coolingLoad.sensible)}`, color: '#90caf9' },
    { text: `Latent: ${formatLoad(coolingLoad.latent)}`, color: '#80deea' },
    { text: `Est. CFM: ${cfm}`, color: '#4caf50' },
  ];

  // Measure text
  ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;

  let maxWidth = 0;
  lines.forEach(line => {
    const metrics = ctx.measureText(line.text);
    maxWidth = Math.max(maxWidth, metrics.width);
  });

  const boxWidth = maxWidth + padding * 4;
  const boxHeight = lines.length * lineHeight + padding * 2;
  const boxX = centroid.x - boxWidth / 2;
  const boxY = centroid.y - boxHeight / 2;

  // Draw background with highlight
  ctx.fillStyle = 'rgba(0, 100, 150, 0.85)';
  ctx.beginPath();
  ctx.roundRect(boxX, boxY, boxWidth, boxHeight, cornerRadius);
  ctx.fill();

  // Draw highlighted border
  ctx.strokeStyle = '#00c8ff';
  ctx.lineWidth = 2 / scale;
  ctx.stroke();

  // Draw text
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  lines.forEach((line, index) => {
    const textY = boxY + padding + lineHeight * (index + 0.5);
    ctx.fillStyle = line.color;
    ctx.fillText(line.text, centroid.x, textY);
  });
}

/**
 * Draw mini load summary in corner of canvas
 */
export function drawLoadSummary(ctx, rooms, roomsData, designConditions, canvasWidth, canvasHeight, scale) {
  // Calculate totals
  let totalHeating = 0;
  let totalCooling = 0;

  const roomDataMap = {};
  (roomsData || []).forEach(rd => {
    if (rd.roomId) {
      roomDataMap[rd.roomId] = rd;
    }
  });

  (rooms || []).forEach(room => {
    let roomData = roomDataMap[room.id];

    if (!roomData) {
      const points = room.points || room.vertices;
      let area = 0;
      if (points && points.length >= 3) {
        for (let i = 0; i < points.length; i++) {
          const j = (i + 1) % points.length;
          area += points[i].x * points[j].y - points[j].x * points[i].y;
        }
        area = Math.abs(area) / 2 / (40 * 40);
      }

      roomData = {
        name: room.name || 'bedroom',
        conditioned: 'yes',
        floorArea: Math.round(area) || 0,
        ceilingHeight: 8,
        ceilingType: 'below-vented-attic',
        windows: [],
        exteriorWalls: [],
        floorType: 'slab',
        doors: [],
      };
    }

    if (roomData.conditioned === 'no') return;

    const heating = calculateRoomHeatingLoad(roomData, designConditions);
    const cooling = calculateRoomCoolingLoad(roomData, designConditions);

    totalHeating += heating.total;
    totalCooling += cooling.total;
  });

  if (totalHeating === 0 && totalCooling === 0) return;

  // Position in bottom-right corner
  const padding = 10;
  const boxWidth = 160;
  const boxHeight = 60;
  const boxX = canvasWidth - boxWidth - padding;
  const boxY = canvasHeight - boxHeight - padding;

  // Draw background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
  ctx.beginPath();
  ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 8);
  ctx.fill();

  ctx.strokeStyle = 'rgba(0, 200, 255, 0.5)';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Draw title
  ctx.font = 'bold 11px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = '#00c8ff';
  ctx.textAlign = 'center';
  ctx.fillText('Total Loads', boxX + boxWidth / 2, boxY + 14);

  // Draw loads
  ctx.font = '12px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.textAlign = 'left';

  ctx.fillStyle = '#ff9800';
  ctx.fillText(`🔥 ${formatLoad(totalHeating)} BTU/hr`, boxX + 10, boxY + 32);

  ctx.fillStyle = '#2196f3';
  ctx.fillText(`❄️ ${formatLoad(totalCooling)} BTU/hr`, boxX + 10, boxY + 48);

  // Draw tons
  ctx.textAlign = 'right';
  ctx.fillStyle = '#4caf50';
  const tons = (totalCooling / 12000).toFixed(1);
  ctx.fillText(`${tons} tons`, boxX + boxWidth - 10, boxY + 48);
}
